import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Group,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import type { DatesRangeValue } from "@mantine/dates";
import { IconDownload, IconSettings } from "@tabler/icons-react";
import CommunicationLogDetailDrawer from "../../components/admin/communication-logs/CommunicationLogDetailDrawer";
import CommunicationLogFiltersPanel, {
  CommunicationLogDateRange,
  CommunicationLogFilterActions,
  CommunicationLogFilterValues,
} from "../../components/admin/communication-logs/CommunicationLogFiltersPanel";
import CommunicationLogTable from "../../components/admin/communication-logs/CommunicationLogTable";
import CommunicationSafetySummary from "../../components/admin/communication-logs/CommunicationSafetySummary";
import RedirectBypassPrefixModal from "../../components/admin/communication-logs/RedirectBypassPrefixModal";
import { useCommunicationLogs } from "../../hooks/admin/useCommunicationLogs";
import { useCommunicationRedirectSettings } from "../../hooks/admin/useCommunicationRedirectSettings";
import { useHasPermission } from "../../hooks/useHasPermission";
import {
  CommunicationChannel,
  CommunicationLogFilters,
  CommunicationRoutingDecision,
  CommunicationStatus,
  communicationLogService,
} from "../../services/admin/communicationLog.service";
import { parseDateOnly } from "../../utils/dateOnly";

export default function CommunicationLogsPage() {
  const [page, setPage] = useState(1);
  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [routingDecision, setRoutingDecision] = useState("");
  const [communicationType, setCommunicationType] = useState("");
  const [recipient, setRecipient] = useState("");
  const [caseNumber, setCaseNumber] = useState("");
  const [dateRange, setDateRange] = useState<CommunicationLogDateRange>([
    null,
    null,
  ]);
  const [drawerLogId, setDrawerLogId] = useState<string | null>(null);
  const [settingsOpened, setSettingsOpened] = useState(false);
  const canManageSettings = useHasPermission(
    "communication_redirect_settings.manage"
  );

  const filters = useMemo<CommunicationLogFilters>(
    () => ({
      page,
      limit: 50,
      ...(channel && { channel: channel as CommunicationChannel }),
      ...(status && { status: status as CommunicationStatus }),
      ...(routingDecision && {
        routingDecision: routingDecision as CommunicationRoutingDecision,
      }),
      ...(communicationType && { communicationType }),
      ...(recipient && { recipient }),
      ...(caseNumber && { caseNumber }),
      ...(dateRange[0] && { startDate: dateRange[0] }),
      ...(dateRange[1] && { endDate: dateRange[1] }),
    }),
    [
      caseNumber,
      channel,
      communicationType,
      dateRange,
      page,
      recipient,
      routingDecision,
      status,
    ]
  );
  const logsQuery = useCommunicationLogs(filters);
  const settingsQuery = useCommunicationRedirectSettings();
  const logs = useMemo(() => logsQuery.data?.data ?? [], [logsQuery.data]);
  const total = useMemo(() => logsQuery.data?.total ?? 0, [logsQuery.data]);
  const totalPages = useMemo(() => {
    const limit = logsQuery.data?.limit ?? 50;
    return Math.max(1, Math.ceil(total / limit));
  }, [logsQuery.data, total]);
  const filterValues = useMemo<CommunicationLogFilterValues>(
    () => ({
      channel,
      status,
      routingDecision,
      communicationType,
      recipient,
      caseNumber,
      dateRange,
    }),
    [
      caseNumber,
      channel,
      communicationType,
      dateRange,
      recipient,
      routingDecision,
      status,
    ]
  );
  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        channel ||
          status ||
          routingDecision ||
          communicationType ||
          recipient ||
          caseNumber ||
          dateRange[0]
      ),
    [
      caseNumber,
      channel,
      communicationType,
      dateRange,
      recipient,
      routingDecision,
      status,
    ]
  );

  const handleChannelChange = useCallback((value: string) => {
    setChannel(value);
    setPage(1);
  }, []);
  const handleStatusChange = useCallback((value: string) => {
    setStatus(value);
    setPage(1);
  }, []);
  const handleRoutingChange = useCallback((value: string) => {
    setRoutingDecision(value);
    setPage(1);
  }, []);
  const handleTypeChange = useCallback((value: string) => {
    setCommunicationType(value);
    setPage(1);
  }, []);
  const handleRecipientChange = useCallback((value: string) => {
    setRecipient(value);
    setPage(1);
  }, []);
  const handleCaseNumberChange = useCallback((value: string) => {
    setCaseNumber(value);
    setPage(1);
  }, []);
  const handleDateRangeChange = useCallback((value: DatesRangeValue) => {
    const startDate = parseDateOnly(value[0]);
    const endDate = parseDateOnly(value[1]);
    const hasInvalidValue =
      (value[0] !== null && !startDate) || (value[1] !== null && !endDate);

    setDateRange(
      hasInvalidValue ? [null, null] : [startDate ?? null, endDate ?? null]
    );
    setPage(1);
  }, []);
  const filterActions = useMemo<CommunicationLogFilterActions>(
    () => ({
      setChannel: handleChannelChange,
      setStatus: handleStatusChange,
      setRoutingDecision: handleRoutingChange,
      setCommunicationType: handleTypeChange,
      setRecipient: handleRecipientChange,
      setCaseNumber: handleCaseNumberChange,
      setDateRange: handleDateRangeChange,
    }),
    [
      handleCaseNumberChange,
      handleChannelChange,
      handleDateRangeChange,
      handleRecipientChange,
      handleRoutingChange,
      handleStatusChange,
      handleTypeChange,
    ]
  );
  const handleClearFilters = useCallback(() => {
    setChannel("");
    setStatus("");
    setRoutingDecision("");
    setCommunicationType("");
    setRecipient("");
    setCaseNumber("");
    setDateRange([null, null]);
    setPage(1);
  }, []);
  const handleExport = useCallback(() => {
    window.open(communicationLogService.exportUrl(filters));
  }, [filters]);
  const handleViewLog = useCallback(
    (logId: string) => setDrawerLogId(logId),
    []
  );
  const handleCloseDrawer = useCallback(() => setDrawerLogId(null), []);
  const handleOpenSettings = useCallback(() => setSettingsOpened(true), []);
  const handleCloseSettings = useCallback(() => setSettingsOpened(false), []);
  const handlePageChange = useCallback((value: number) => {
    setPage(value);
  }, []);
  const handleRetrySettings = useCallback(() => {
    settingsQuery.refetch();
  }, [settingsQuery]);

  return (
    <Container fluid py="xl">
      <Box maw={1600} mx="auto">
        <Stack gap="xl">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Communication Logs</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Outbound communications across email, HL7, fax, SMS, and voice
              </Text>
            </div>
            <Group gap="sm">
              {canManageSettings && (
                <Button
                  leftSection={<IconSettings size={16} />}
                  variant="light"
                  color="orange"
                  onClick={handleOpenSettings}
                >
                  Redirect bypass prefixes
                </Button>
              )}
              <Button
                leftSection={<IconDownload size={16} />}
                variant="light"
                onClick={handleExport}
              >
                Export CSV
              </Button>
            </Group>
          </Group>

          <CommunicationSafetySummary
            settings={settingsQuery.data}
            isLoading={settingsQuery.isLoading}
            isError={settingsQuery.isError}
            onRetry={handleRetrySettings}
          />
          <CommunicationLogFiltersPanel
            values={filterValues}
            actions={filterActions}
            hasActiveFilters={hasActiveFilters}
            onClear={handleClearFilters}
          />
          <CommunicationLogTable
            logs={logs}
            total={total}
            totalPages={totalPages}
            page={page}
            isLoading={logsQuery.isLoading}
            isError={logsQuery.isError}
            onPageChange={handlePageChange}
            onView={handleViewLog}
          />
        </Stack>
      </Box>

      <CommunicationLogDetailDrawer
        logId={drawerLogId}
        opened={Boolean(drawerLogId)}
        onClose={handleCloseDrawer}
      />
      <RedirectBypassPrefixModal
        opened={settingsOpened}
        onClose={handleCloseSettings}
      />
    </Container>
  );
}
